import { z } from 'zod';

/**
 * Server-generated UUID (v4 or v7), read in lower case as PostgreSQL writes it: an id typed in
 * upper case is the same id, sorts like it (rows are locked in id order) and compares equal to it.
 */
export const Id = z.uuid().transform((id) => id.toLowerCase());
export type Id = z.infer<typeof Id>;

/** Instance identifier, e.g. `inst_7f3a9c21d4e5…`. Fixed for the lifetime of a customer installation. */
export const InstanceId = z.string().regex(/^inst_[0-9a-z]{12,40}$/);
export type InstanceId = z.infer<typeof InstanceId>;

const MAX_BIGINT = 2n ** 63n - 1n;

/** Ordering counters are PostgreSQL bigint and travel as decimal strings. */
export const Seq = z
  .string()
  .regex(/^(0|[1-9]\d{0,18})$/)
  .refine((value) => BigInt(value) <= MAX_BIGINT, 'larger than a PostgreSQL bigint');
export type Seq = z.infer<typeof Seq>;

/** UTC timestamp in ISO 8601 with `Z`. */
export const Timestamp = z.iso.datetime();
export type Timestamp = z.infer<typeof Timestamp>;

/** Opaque pagination cursor. */
export const Cursor = z.string().min(1).max(512);
export type Cursor = z.infer<typeof Cursor>;

export const Limit = (max: number, fallback: number) => z.coerce.number().int().min(1).max(max).default(fallback);

/** Text a person types for a name or title: trimmed, one line, no control characters (it ends up in mail headers and system lines). */
export function singleLine(options: { min?: number; max: number }) {
  return z
    .string()
    .trim()
    .min(options.min ?? 0)
    .max(options.max)
    .refine((value) => !/\p{Cc}/u.test(value), 'must be a single line without control characters');
}

export const CompanyRole = z.enum(['owner', 'admin', 'member']);
export type CompanyRole = z.infer<typeof CompanyRole>;

export const AccountStatus = z.enum(['active', 'deactivated']);
export type AccountStatus = z.infer<typeof AccountStatus>;

/** Avatar gradient slot from the design (p7 is the neutral gray used for deactivated people). */
export const Palette = z.enum(['p1', 'p2', 'p3', 'p4', 'p5', 'p6', 'p7']);
export type Palette = z.infer<typeof Palette>;

export const ClientKind = z.enum(['web', 'desktop']);
export type ClientKind = z.infer<typeof ClientKind>;

export const ClientPlatform = z.enum(['windows', 'macos', 'linux', 'web']);
export type ClientPlatform = z.infer<typeof ClientPlatform>;

export const ClientInfo = z.object({
  kind: ClientKind,
  platform: ClientPlatform,
  /** Shown in "로그인된 기기 관리", e.g. "이 PC · macOS" or "Chrome". */
  deviceLabel: z.string().trim().min(1).max(80),
  appVersion: z.string().max(32).optional(),
});
export type ClientInfo = z.infer<typeof ClientInfo>;

/** Compares two Seq strings numerically. */
export function compareSeq(a: Seq, b: Seq): number {
  const x = BigInt(a);
  const y = BigInt(b);
  return x < y ? -1 : x > y ? 1 : 0;
}

export function maxSeq(a: Seq, b: Seq): Seq {
  return compareSeq(a, b) >= 0 ? a : b;
}

/**
 * Email normalisation rule: trim, Unicode NFC, lower-case and
 * IDNA-encode the domain only. The local part is kept as typed — addresses are
 * never merged by lower-casing everything or by provider-specific dot/plus
 * rules. Returns null when the address is not plausibly valid.
 */
export function normalizeEmail(raw: string): string | null {
  const value = raw.trim().normalize('NFC');
  if (value.length < 3 || value.length > 254 || /\s/.test(value)) return null;
  const at = value.lastIndexOf('@');
  if (at <= 0 || at === value.length - 1) return null;
  const local = value.slice(0, at);
  const domainInput = value.slice(at + 1).toLowerCase();
  if (local.length > 64 || local.includes('@')) return null;
  if (!/^[^\s"(),:;<>[\\\]]+$/.test(local) || local.startsWith('.') || local.endsWith('.') || local.includes('..')) {
    return null;
  }
  let domain: string;
  try {
    domain = new URL(`http://${domainInput}`).hostname;
  } catch {
    return null;
  }
  if (!/^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(domain) || domain.split('.').some((label) => label.startsWith('-') || label.endsWith('-'))) {
    return null;
  }
  return `${local}@${domain}`;
}

export const Email = z
  .string()
  .max(254)
  .transform((value, ctx) => {
    const normalized = normalizeEmail(value);
    if (!normalized) {
      ctx.addIssue({ code: 'custom', message: 'invalid email address' });
      return z.NEVER;
    }
    return normalized;
  });

/**
 * Two-letter avatar label used across the design: Hangul names use the given
 * name ("김서연" → "서연"); other names use initials ("Alex Kim" → "AK").
 */
export function shortNameOf(displayName: string): string {
  const name = displayName.trim();
  if (!name) return '?';
  if (/^[가-힣]+$/.test(name)) return name.length >= 3 ? name.slice(-2) : name;
  const words = name.split(/\s+/).filter(Boolean);
  if (words.length >= 2) return `${[...words[0]!][0]!}${[...words[1]!][0]!}`.toUpperCase();
  return [...name].slice(0, 2).join('').toUpperCase();
}
